/* eslint-disable import/no-cycle -- pre-existing dependency cycles in the store wiring; this change only whitelists a new persisted slice */
import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import LogRocket from 'logrocket'
import { createMigrate, createTransform, persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import createSagaMiddleware from 'redux-saga'
// eslint-disable-next-line no-restricted-imports -- RTK doesn't re-export setGlobalDevModeChecks
import { setGlobalDevModeChecks } from 'reselect'

import api from 'api'
import { rtkQueryErrorLogger } from 'api/errorLogger'
import { refusedRefetchRetry } from 'api/refusedRefetchRetry'
import { textqlApi } from 'api/textqlApi'
import Constants from 'Constants'
import rootReducer from 'middleware/reducers'
import { getTransientExceptionsFilterState } from 'slices/exceptions/exceptionsFiltersSlice'
import { startCardVisibilitySync } from 'slices/route/cardVisibilitySync'
import { getStopDetailsPopoverClosedState } from 'slices/route/routeSlice'

import migrations from './migrations'
import { ignoreFrustratingCalls, ignoreLargeCalls, ignoreLargeState } from './settings'

import type { StopDetailsPopoverState } from 'slices/route/routeSliceTypes'

const { LOGROCKET_ID } = Constants

interface PersistedRouteState extends Record<string, unknown> {
  isTimelineModeActive?: boolean
  stopDetailsPopover?: StopDetailsPopoverState
}

// Transform to exclude transient UI state (video player, hover popover) from persistence
const RouteTransform = createTransform(
  (inboundState: PersistedRouteState, key) => {
    if (key === 'Route') {
      return {
        ...inboundState,
        isTimelineModeActive: false, // Never persist video player open state
        stopDetailsPopover: getStopDetailsPopoverClosedState(), // Never persist hover popover open state
      }
    }
    return inboundState
  },
  (outboundState: PersistedRouteState, key) => {
    if (key === 'Route') {
      return {
        ...outboundState,
        isTimelineModeActive: false, // Always start with video player closed
        stopDetailsPopover: getStopDetailsPopoverClosedState(), // Always start with hover popover closed
      }
    }
    return outboundState
  },
  { whitelist: ['Route'] }
)

// The filters persist; an expanded row would rehydrate pointing at a detection that has moved.
const ExceptionsFiltersTransform = createTransform(
  (inboundState: Record<string, unknown>) => ({ ...inboundState, ...getTransientExceptionsFilterState() }),
  (outboundState: Record<string, unknown>) => ({ ...outboundState, ...getTransientExceptionsFilterState() }),
  { whitelist: ['exceptionsFilters'] }
)

const persistConfig = {
  key: 'root',
  storage,
  whitelist: [
    'AuthReducer',
    'BillingReducer',
    'Route',
    'RouteManagerReducer',
    // persistentMasterRoute and persistentBoardV2Card absent by design — startCardVisibilitySync gives them their own keys.
    'persistentBoardV2View',
    'persistentListView',
    'persistentRouteViewPref',
    'persistentRouteProductivityColumns',
    'CustomerDetails',
    'configuredServiceDetails',
    'locations',
    'persistentWorkOrdersReviewV2',
    'persistentLateFeeReview',
    'persistentPaymentBatches',
    'persistentInvoices',
    'persistentDisposalTicketsReview',
    'accounts',
    'home',
    'LoginPersist',
    'pricingDrawer',
    'reportingDrawer',
    'persistentDisposalFees',
    'persistentLateFees',
    'persistentRentalFees',
    'persistentDisposalSites',
    'persistentCustomerDetailsServices',
    'persistentCustomerDetailsLocations',
    'inventoryDrawer',
    'visionDrawer',
    'exceptionsFilters',
    'inspectionsFilters',
    'persistentGroupsPageView',
    'persistentInventoryContainers',
    'persistentRouteOpsStrategy',
    'persistentMapPreferences',
    'persistentOptimizationJobs',
    'persistentAiReportsChat',
  ],
  transforms: [RouteTransform, ExceptionsFiltersTransform],
  migrate: createMigrate(migrations, { debug: process.env.NODE_ENV !== 'production' }),
  version: 12,
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

const sagaMiddleware = createSagaMiddleware()

const middlewares = [sagaMiddleware, rtkQueryErrorLogger, api.middleware, refusedRefetchRetry, textqlApi.middleware]

if (LOGROCKET_ID) {
  middlewares.push(
    LogRocket.reduxMiddleware({
      actionSanitizer: action => {
        if ([...ignoreLargeCalls, ...ignoreFrustratingCalls].includes(action?.meta?.arg?.endpointName)) {
          return null
        }
        return action
      },
      stateSanitizer: state => {
        return {
          ...state,
          ...ignoreLargeState,
        }
      },
    })
  )
}

// Legacy selectors return fresh arrays by design; reselect 5's dev checks would warn on every call.
setGlobalDevModeChecks({ inputStabilityCheck: 'never', identityFunctionCheck: 'never' })

const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware => getDefaultMiddleware({ serializableCheck: false }).concat(middlewares),
  devTools: process.env.NODE_ENV !== 'production',
})
const persistor = persistStore(store)

startCardVisibilitySync(store)

// Without this, `refetchOnFocus` / `refetchOnReconnect` are accepted by every query
// and silently do nothing. Opting in stays per-query — this only makes the flags work.
setupListeners(store.dispatch)

export { store, persistor, sagaMiddleware }
